import { ApiInstance } from '@/lib/api'
import type { CursorParams, Paginated } from '@/lib/pagination'

export type ChatConversationOrigin = 'site' | 'instagram' | 'whatsapp'

export type ChatMessageMediaType = 'text' | 'audio' | 'image' | 'sticker' | 'gif' | 'property' | 'document'

export type PropertyCardPurpose = 'sale' | 'rent' | 'sale-rent'

export type PropertyCard = {
  realStateId: string
  code: number | null
  title: string
  url: string | null
  imageUrl: string | null
  purpose: PropertyCardPurpose | null
  priceLabel: string | null
  secondaryPriceLabel: string | null
  location: string | null
  specs: string[]
}

export type ChatMessageSender = 'visitor' | 'agent' | 'bot'

export type ChatBotPausedReason = 'manual' | 'agent_message' | 'claimed' | 'handoff'

export type ChatConversationBotState = {
  botPaused?: boolean
  botPausedAt?: string | null
  botPausedReason?: ChatBotPausedReason | null
  botPausedBy?: string | null
  botHandoffSummary?: string | null
}

export type ChatConversation = {
  id: string
  companyId: string
  customerId?: string
  visitorName: string
  visitorPhone?: string
  visitorAvatar?: string | null
  instagramId?: string | null
  instagramConversationId?: string | null
  whatsappWaId?: string | null
  whatsappPhoneNumberId?: string | null
  lastVisitorMessageAt?: string | null
  realStateId?: string
  realStateTitle?: string
  closed: boolean
  closedAt?: string | null
  assignedUserId?: string | null
  assignedUserName?: string | null
  sessionsEnded?: string[]
  pendingNameUpdate?: { newName: string; suggestedAt: string } | null
  lastVisitorSeenAt?: string | null
  visitorCurrentPath?: string | null
  visitorCurrentTitle?: string | null
  visitorCurrentUrl?: string | null
  lastMessageAt?: string
  lastMessagePreview?: string
  lastMessageSender?: ChatMessageSender
  unreadByAgent: number
  origin?: ChatConversationOrigin
  createdAt: string
  updatedAt: string
} & ChatConversationBotState

export type ChatMessage = {
  id: string
  conversationId: string
  companyId: string
  sender: ChatMessageSender
  authorId?: string
  authorName?: string
  clientId?: string
  text: string
  mediaType?: ChatMessageMediaType
  mediaUrl?: string | null
  fileName?: string | null
  card?: PropertyCard | null
  transcript?: string | null
  instagramMessageId?: string | null
  whatsappMessageId?: string | null
  seenAt?: string | null
  createdAt: string
}

export type ChatMessageStatus = 'sending' | 'sent' | 'read'

export type ChatLocalMessage = ChatMessage & { status: ChatMessageStatus }

export function formatChatOrigin(origin?: ChatConversationOrigin | null): string {
  switch (origin) {
    case 'instagram':
      return 'Instagram'
    case 'whatsapp':
      return 'WhatsApp'
    case 'site':
      return 'Site'
    default:
      return 'Site'
  }
}

const PROPERTY_CARD_PURPOSES: PropertyCardPurpose[] = ['sale', 'rent', 'sale-rent']

const cardText = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null)

const cardLink = (value: unknown): string | null => {
  const text = cardText(value)
  return text && /^https?:\/\//i.test(text) ? text : null
}

export function propertyCardOf(message: Pick<ChatMessage, 'mediaType'> & { card?: unknown }): PropertyCard | null {
  if (message.mediaType !== 'property' || !message.card || typeof message.card !== 'object') return null
  const card = message.card as Record<string, unknown>
  const realStateId = cardText(card.realStateId)
  const title = cardText(card.title)
  if (!realStateId || !title) return null
  const code = typeof card.code === 'number' && Number.isFinite(card.code) ? card.code : null
  const purpose = PROPERTY_CARD_PURPOSES.find(option => option === card.purpose) ?? null
  const specs = Array.isArray(card.specs) ? card.specs.map(cardText).filter((spec): spec is string => !!spec) : []
  return {
    realStateId,
    code,
    title,
    url: cardLink(card.url),
    imageUrl: cardLink(card.imageUrl),
    purpose,
    priceLabel: cardText(card.priceLabel),
    secondaryPriceLabel: cardText(card.secondaryPriceLabel),
    location: cardText(card.location),
    specs
  }
}

export const countsAsUnread = (sender?: string | null): boolean => sender === 'visitor'

export const CHAT_BOT_PAUSE_REASON: Record<ChatBotPausedReason, string> = {
  manual: 'Pausado manualmente',
  agent_message: 'Um corretor respondeu',
  claimed: 'Um corretor entrou na conversa',
  handoff: 'Transferido para um corretor'
}

const isBotPausedReason = (value: unknown): value is ChatBotPausedReason =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(CHAT_BOT_PAUSE_REASON, value)

export const botPauseReasonLabel = (reason?: string | null): string | null => (isBotPausedReason(reason) ? CHAT_BOT_PAUSE_REASON[reason] : null)

export function chatBotPatch(payload: unknown): ChatConversationBotState {
  const source = (payload ?? {}) as Record<string, unknown>
  const text = (value: unknown) => (typeof value === 'string' && value ? value : null)
  return {
    botPaused: source.botPaused === true,
    botPausedAt: text(source.botPausedAt),
    botPausedReason: isBotPausedReason(source.botPausedReason) ? source.botPausedReason : null,
    botPausedBy: text(source.botPausedBy),
    botHandoffSummary: text(source.botHandoffSummary)
  }
}

export async function listChatConversations(params?: CursorParams & { search?: string }) {
  const { data } = await ApiInstance.get<Paginated<ChatConversation>>('/chat/conversations', { params })
  return data
}

export async function getChatConversation(id: string) {
  const { data } = await ApiInstance.get<ChatConversation>(`/chat/conversations/${id}`)
  return data
}

export const UNSUPPORTED_NOTICE = 'Formato ainda não suportado pela plataforma.'

const UNSUPPORTED_SUFFIX = `\n${UNSUPPORTED_NOTICE}`

export function splitUnsupportedNotice(text?: string | null): { text: string; unsupported: boolean } {
  const value = text ?? ''
  return value.endsWith(UNSUPPORTED_SUFFIX)
    ? { text: value.slice(0, -UNSUPPORTED_SUFFIX.length), unsupported: true }
    : { text: value, unsupported: false }
}

const MEDIA_PREVIEW: Partial<Record<ChatMessageMediaType, string>> = {
  image: 'Foto',
  audio: 'Áudio',
  sticker: 'Figurinha',
  gif: 'GIF',
  document: 'Documento'
}

const PLACEHOLDER_TEXT = /^\[(image|audio|sticker|gif|document)\]$/

export function messagePreview(message: Pick<ChatMessage, 'text' | 'mediaType'> & { card?: unknown }): string {
  const card = propertyCardOf(message)
  if (card) return `Imóvel: ${card.title}`
  const text = splitUnsupportedNotice(message.text).text.trim()
  const media = message.mediaType ? MEDIA_PREVIEW[message.mediaType] : undefined
  if (media && (!text || PLACEHOLDER_TEXT.test(text))) return media
  return text
}
